import {ErrorHandler, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {ErrorInterceptor} from "./_guards/error.interceptor";
import {JwtModule, JwtModuleOptions} from '@auth0/angular-jwt';
import {environment} from "../environments/environment";

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule, NativeDateAdapter} from "@angular/material/core";
import {HTTP_INTERCEPTORS, HttpClientModule} from "@angular/common/http";
import {DashboardComponent} from './dashboard/dashboard.component';
import {AuthviewComponent} from "./authview/authview.component";
import {LoginComponent} from "./login/login.component";
import {NotfoundComponent} from "./notfound/notfound.component";
import {MatCardModule} from "@angular/material/card";
import {MatInputModule} from "@angular/material/input";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatSidenavModule} from "@angular/material/sidenav";
import {MatListModule} from "@angular/material/list";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {MatExpansionModule} from "@angular/material/expansion";
import {MatTableModule} from "@angular/material/table";
import {MatTabsModule} from "@angular/material/tabs";
import {MatSelectModule} from "@angular/material/select";
import {MatSliderModule} from "@angular/material/slider";
import {MatPaginatorModule} from "@angular/material/paginator";
import {MatSnackBarModule} from "@angular/material/snack-bar";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatRadioModule} from "@angular/material/radio";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {PickerComponent} from "@ctrl/ngx-emoji-mart";
import {EmojiMenuComponent} from "./shared/emoji-menu/emoji-menu.component";
import {SettingsComponent} from './shared/settings/settings.component';
import {CreatePostComponent} from './shared/create-post/create-post.component';
import {ReplyFieldComponent} from './shared/reply-field/reply-field.component';
import {UnauthviewComponent} from './unauthview/unauthview.component';
import {PasswordValidatorComponent} from "./shared/password-validator/password-validator.component";
import {MatStepperModule} from "@angular/material/stepper";
import {ScrollNearEndDirective} from "./_directives/scroll-near-end.directive";
import {TermsComponent} from './terms/terms.component';
import {PrivacyComponent} from './privacy/privacy.component';
import {ContactComponent} from './contact/contact.component';
import {BadgesComponent} from './shared/badges/badges.component';
import {LevelsComponent} from './shared/levels/levels.component';
import {SharedModule} from './shared/shared.module';
import {DashboardFeedComponent} from './feed/dashboard-feed/dashboard-feed.component';
import {PostCardComponent} from './feed/post-card/post-card.component';
import {CommentsComponent} from './shared/comments/comments.component';
import {CommentComponent} from './shared/comment/comment.component';
import {CommentsListComponent} from './shared/comments-list/comments-list.component';
import {FeedComponent} from './feed/feed.component';
import {PostContentComponent} from './shared/post-content/post-content.component';
import {PasswordResetComponent} from './password-reset/password-reset.component';
import {PointsLeaderboardComponent} from './feed/points-leaderboard/points-leaderboard.component';
import {InviteAcceptComponent} from './invite-accept/invite-accept.component';
import {RemoveDashPipe} from "./_pipes/remove-dash.pipe";
import {CdkCopyToClipboard} from "@angular/cdk/clipboard";
import {ImageCropperComponent} from "ngx-image-cropper";
import {CKEditorModule} from "@ckeditor/ckeditor5-angular";
import {LoadingComponent} from "./shared/loading/loading.component";
import {CommunicationPreferencesComponent} from './communication-preferences/communication-preferences.component';
import {PassthroughComponent} from "./passthrough/passthrough.component";
import {AppTitlePrefix} from './_services/dynamic-title.service';
import {TitleStrategy} from '@angular/router';
import {formatDate} from "@angular/common";
import {EmblaCarouselDirective} from "embla-carousel-angular";
import {NotificationRoundPipe} from "./_pipes/notification-round.pipe";
import * as Sentry from "@sentry/angular";
import {ResizableIframeDirective} from "./_directives/resizable-iframe.directive";
import {CountAnimationDirective} from "./_directives/count-animation.directive";
import {InfiniteScrollDirective} from "ngx-infinite-scroll";
import {NgxMentionsModule} from "ngx-mentions";
import {MentionFormatPipe} from "./_pipes/mention-format.pipe";
import {SocketIoConfig, SocketIoModule} from "ngx-socket-io";

const socketConfig: SocketIoConfig = {url: environment.SocketIoUrl, options: {}};

export function jwtTokenGetter(): string | null {
    return localStorage.getItem('access_token');
}

const jwtConfig: JwtModuleOptions = {
    config: {
        tokenGetter: jwtTokenGetter,
        allowedDomains: environment.whitelist,
        disallowedRoutes: environment.blacklist,
        skipWhenExpired: false,
    }
};

export const PICK_FORMATS = {
    parse: {dateInput: {month: 'short', year: 'numeric', day: 'numeric'}},
    display: {
        dateInput: 'input',
        monthYearLabel: {year: 'numeric', month: 'short'},
        dateA11yLabel: {year: 'numeric', month: 'long', day: 'numeric'},
        monthYearA11yLabel: {year: 'numeric', month: 'long'}
    }
};

class PickDateAdapter extends NativeDateAdapter {
    override format(date: Date, displayFormat: Object): string {
        if (displayFormat === 'input') {
            return formatDate(date, 'dd/MM/yyyy', this.locale);
        } else {
            return date.toDateString();
        }
    }
}

@NgModule({
    declarations: [
        AppComponent,
        DashboardComponent,
        AuthviewComponent,
        LoginComponent,
        NotfoundComponent,
        EmojiMenuComponent,
        SettingsComponent,
        CreatePostComponent,
        ReplyFieldComponent,
        UnauthviewComponent,
        TermsComponent,
        PrivacyComponent,
        ContactComponent,
        BadgesComponent,
        LevelsComponent,
        FeedComponent,
        DashboardFeedComponent,
        PostCardComponent,
        CommentsComponent,
        CommentComponent,
        CommentsListComponent,
        PostContentComponent,
        PasswordResetComponent,
        PointsLeaderboardComponent,
        InviteAcceptComponent,
        LoadingComponent,
        CommunicationPreferencesComponent,
        PassthroughComponent,
        ResizableIframeDirective,
    ],
    imports: [
        JwtModule.forRoot(jwtConfig),
        SharedModule,
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MatInputModule,
        MatFormFieldModule,
        MatCardModule,
        HttpClientModule,
        MatProgressSpinnerModule,
        MatSidenavModule,
        MatNativeDateModule,
        MatSlideToggleModule,
        MatAutocompleteModule,
        MatExpansionModule,
        MatListModule,
        MatDialogModule,
        MatTableModule,
        MatTabsModule,
        MatSelectModule,
        MatSliderModule,
        MatPaginatorModule,
        MatSnackBarModule,
        MatCheckboxModule,
        MatRadioModule,
        MatButtonToggleGroup,
        MatButtonToggle,
        PickerComponent,
        MatStepperModule,
        ScrollNearEndDirective,
        RemoveDashPipe,
        CdkCopyToClipboard,
        ImageCropperComponent,
        CKEditorModule,
        EmblaCarouselDirective,
        NotificationRoundPipe,
        CountAnimationDirective,
        InfiniteScrollDirective,
        NgxMentionsModule,
        MentionFormatPipe,
        SocketIoModule.forRoot(socketConfig),
    ],
    providers: [
        {provide: TitleStrategy, useClass: AppTitlePrefix},
        {provide: MAT_DIALOG_DATA, useValue: {}},
        {provide: MatDialogRef, useValue: {}},
        {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
        {provide: DateAdapter, useClass: PickDateAdapter},
        {provide: MAT_DATE_FORMATS, useValue: PICK_FORMATS},
        {
            provide: ErrorHandler, useValue: Sentry.createErrorHandler(),
        },
    ],
    exports: [
        PasswordValidatorComponent,
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
